import React, { useEffect, useState } from "react";
import { getDirectusClient } from "../../lib/directus";
import { Button, Card, Form, Modal, message } from "antd";
import { readItems, createItem, updateItem, deleteItem, aggregate } from "@directus/sdk";
import BodegasTable from "./components/BodegasTable";
import BodegasForm from "./components/BodegasForm";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";
import validarpermiso from '../../lib/utils';

function BodegasCrud() {
  const permisos = useSelector((state) => state.permisos);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [bodegas, setBodegas] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentBodega, setCurrentBodega] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  // Obtener la lista de bodegas paginadas
  const fetchBodegas = async (page = 1, pageSize = 25) => {
    if (!validarpermiso("bodegas", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();

      // Obtener las bodegas paginadas
      const response = await directus.request(
        readItems("bodegas", {
          fields: ["id", "nombre", "date_created"],
          sort: ["-nombre"],
          limit: pageSize,
          page: page,
        })
      );

      // Obtener el total de registros
      const totalResponse = await directus.request(
        aggregate("bodegas", {
          aggregate: {
            countDistinct: ["*"],
          },
        })
      );

      if (totalResponse && totalResponse.length > 0) {
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: parseInt(totalResponse[0].countDistinct["*"], 10),
        });
      }

      setBodegas(response);
      setHasPermissionError(false);
    } catch (error) {
      console.error("Error fetching bodegas:", error);
      if (error.response?.status === 403 || error.message?.includes('permission')) {
        setHasPermissionError(true);
      }
      message.error("Error al cargar las bodegas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBodegas(pagination.current, pagination.pageSize);
  }, []);

  // Abrir modal para crear o editar bodega
  const showModal = (bodega = null) => {
    setCurrentBodega(bodega);
    form.setFieldsValue(bodega || {});
    setIsModalVisible(true);
  };

  // Cerrar modal
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  // Guardar bodega (crear o actualizar)
  const handleSave = async () => {
    const permissionType = currentBodega ? "update" : "create";
    if (!validarpermiso("bodegas", permissionType, permisos)) {
      message.error(`No tiene permisos para ${currentBodega ? 'editar' : 'crear'} bodegas`);
      return;
    }

    try {
      const values = await form.validateFields();
      setLoading(true);
      const directus = await getDirectusClient();

      if (currentBodega) {
        await directus.request(
          updateItem("bodegas", currentBodega.id, values)
        );
        message.success("Bodega actualizada exitosamente");
      } else {
        await directus.request(
          createItem("bodegas", values)
        );
        message.success("Bodega creada exitosamente");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchBodegas(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error saving bodega:", error);
      message.error("Error al guardar la bodega");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar bodega
  const handleDelete = async (id) => {
    if (!validarpermiso("bodegas", "delete", permisos)) {
      message.error("No tiene permisos para eliminar bodegas");
      return;
    }

    try {
      setLoading(true);
      const directus = await getDirectusClient();
      await directus.request(
        deleteItem("bodegas", id)
      );
      message.success("Bodega eliminada exitosamente");
      fetchBodegas(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error deleting bodega:", error);
      message.error("Error al eliminar la bodega");
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios de paginación
  const handleTableChange = (pagination) => {
    setPagination(pagination);
    fetchBodegas(pagination.current, pagination.pageSize);
  };

  return (
    <Layouts>
      <Card
        title="Gestión de Bodegas"
        extra={
          validarpermiso("bodegas", "create", permisos) && !hasPermissionError && (
            <Button 
              type="primary" 
              onClick={() => showModal()}
              disabled={hasPermissionError}
            >
              Crear Bodega
            </Button>
          )
        }
      >
        {!hasPermissionError ? (
          <BodegasTable
            bodegas={bodegas}
            loading={loading}
            pagination={pagination}
            handleTableChange={handleTableChange}
            showModal={showModal}
            handleDelete={handleDelete}
            permisos={permisos}
          />
        ) : (
          <h3>No tiene permisos para ver bodegas</h3>
        )}
      </Card>

      <Modal
        title={currentBodega ? "Editar Bodega" : "Crear Bodega"}
        open={isModalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        confirmLoading={loading}
      >
        <BodegasForm form={form} />
      </Modal>
    </Layouts>
  );
}

export default BodegasCrud;